/**
 * SINGLE-VALUE HIERARCHY CONTEXT — the shared rule and labels behind (a) which levels the grid
 * still renders as tree rows and (b) the consolidated context row that carries the levels which
 * resolve to ONE value. FrsGridView is the table behind the FRS view, the Installer view and the
 * Delivery view, so all three read this one module: same rule, same separator, same label format.
 */
export const HIER_LEVELS = [
  { id: "bldg", field: "building", label: (v) => (/^building\s/i.test(v) ? v : `Building ${v}`) },
  { id: "floor", field: "floor", label: (v) => (/^floor\s/i.test(v) ? v : `Floor ${v}`) },
  { id: "area", field: "area", label: (v) => (/^area\s/i.test(v) ? v : `Area ${v}`) },
];

const val = (row, field) => String(row?.[field] ?? "").trim();

/**
 * A level RENDERS while it still separates rows (>= 2 distinct values inside at least one rendered
 * parent) — unchanged. A level that does not render AND carries exactly one value across the whole
 * filtered set is returned in `singles` so it can be shown instead of disappearing.
 */
export function levelVisibility(rows) {
  const shown = [];
  const singles = [];
  for (let li = 0; li < HIER_LEVELS.length; li++) {
    const byParent = new Map();
    for (const r of rows) {
      const pk = HIER_LEVELS.slice(0, li).map((L, i) => (shown[i] ? val(r, L.field) : "")).join("||");
      if (!byParent.has(pk)) byParent.set(pk, new Set());
      byParent.get(pk).add(val(r, HIER_LEVELS[li].field));
    }
    shown[li] = [...byParent.values()].some((set) => set.size >= 2);
    if (!shown[li]) {
      const values = new Set(rows.map((r) => val(r, HIER_LEVELS[li].field)).filter(Boolean));
      if (values.size === 1) singles.push({ ...HIER_LEVELS[li], value: [...values][0] });
    }
  }
  return { shown, singles };
}

/** The scope crumb: only when the whole filtered set is one scope, ahead of the levels. */
export function scopeCrumb(items) {
  const scopes = new Set((items || []).map((i) => String(i?.scope_type ?? "").trim()).filter(Boolean));
  return scopes.size === 1 ? [...scopes][0] : "";
}
